// Custom Turbo Actions
// This file defines custom client-side Turbo actions that can be triggered from the server
//
// To enable [refresh:custom_refresh] console logging for testing/debugging:
//   In the browser console: window.TURBO_CUSTOM_REFRESH_DEBUG = true
//   Then reload or trigger a refresh. Set to false to disable.

import { Turbo } from "@hotwired/turbo-rails";

Turbo.StreamActions.dismiss_modal = function () {
  const returnUrl = new URL(this.getAttribute("url") || window.location.href, window.location.origin);
  if (returnUrl.origin !== window.location.origin) return;

  window.history.replaceState({}, "", returnUrl.href);
  document.body.removeAttribute("data-turbo-modal-history-advanced");
  if (typeof window.modal?.hide === "function") window.modal.hide();
  requestAnimationFrame(() => {
    document.dispatchEvent(new CustomEvent("canvas-activity:visible"));
  });
};

Turbo.StreamActions.refresh_canvas_signals = function () {
  document.dispatchEvent(new CustomEvent("canvas-signals:refresh"));
};

// Avoid multiple full-page refreshes for the same logical change:
// 1) Only one visit in flight: skip custom_refresh while refreshInProgress.
// 2) CableReady often sends several custom_refresh for one update (one per subscription).
//    The first visit completes (turbo:load); then another CR message runs and triggers
//    a second visit. So after we complete a refresh to URL X, ignore another custom_refresh
//    for X for a short window — long enough to drop duplicate CR, short enough not to skip
//    the next user edit (which has round-trip latency).
let refreshInProgress = false;
let refreshSafetyTimeout = null;
let lastRefreshCompletedAt = 0;
let lastRefreshedUrl = null;
let lastUserInteractionAt = 0;
let removePendingScrollRestore = null;
let pendingRefreshTimer = null;
let pendingCanvasRemoval = null;
let pendingCanvasMutation = null;
let deferredCanvasRefresh = null;

const SAME_URL_COALESCE_MS = 150;
const REFRESH_SAFETY_MS = 15000;
const REFRESH_LOG_PREFIX = "[refresh:custom_refresh]";

setupModalCanvasInnerUpdateTracking();
setupDeferredCanvasViewportPreservation();

// An active modal must survive page morphs.
//
// Mutations answer with a delayed full-page refresh (turbo_refresh_action_tag,
// ~225ms), which morphs the body. The server HTML always carries an EMPTY
// <turbo-frame id="modal"> -- so if the user has already opened the next modal
// by the time the morph lands, morphing that frame strips its src and its
// children: an in-flight open is swallowed (the click does nothing), an
// already-open modal blinks out. Recorded frame by frame in
// canvas_flicker_recording_test: the destroy's refresh at +225ms consistently
// beat Capybara to the next open, and always lost to a human.
//
// The guard: never morph the modal frame while it is in use. When it is idle
// (no src, no children) morphing it is a no-op anyway.
document.addEventListener("turbo:before-morph-element", (event) => {
  const element = event.target;
  if (!(element instanceof Element)) return;
  if (element.tagName !== "TURBO-FRAME" || element.id !== "modal") return;
  if (!element.getAttribute("src") && element.children.length === 0) return;

  event.preventDefault();
});

function refreshLog(...args) {
  if (window.TURBO_CUSTOM_REFRESH_DEBUG) {
    console.log(REFRESH_LOG_PREFIX, ...args);
  }
}

function clearRefreshInProgress({cleanupPendingRestore = true} = {}) {
  refreshInProgress = false;
  lastRefreshCompletedAt = Date.now();
  lastRefreshedUrl = window.location.href;
  if (refreshSafetyTimeout) {
    clearTimeout(refreshSafetyTimeout);
    refreshSafetyTimeout = null;
  }
  if (pendingRefreshTimer) {
    clearTimeout(pendingRefreshTimer);
    pendingRefreshTimer = null;
  }
  if (cleanupPendingRestore && removePendingScrollRestore) {
    removePendingScrollRestore();
    removePendingScrollRestore = null;
  }
  finishOptimisticCanvasRemoval();
  refreshLog("visit finished (turbo:load or error)", { url: normalizeUrl(lastRefreshedUrl) });
}

function setupRefreshListeners() {
  if (setupRefreshListeners.done) return;
  setupRefreshListeners.done = true;
  document.documentElement.addEventListener("turbo:load", () => {
    clearRefreshInProgress({cleanupPendingRestore: false});
  });
  document.documentElement.addEventListener(
    "turbo:fetch-request-error",
    clearRefreshInProgress
  );
  document.addEventListener("pointerdown", markUserInteraction, true);
  document.addEventListener("keydown", markUserInteraction, true);
  document.addEventListener("change", markUserInteraction, true);
  document.addEventListener("submit", markUserInteraction, true);
}

function setupModalCanvasInnerUpdateTracking() {
  document.addEventListener("turbo:before-fetch-request", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !assignedJobActionPath(form.action)) return;
    if (!document.querySelector("#modal-container")) return;

    markCanvasAssignedJobUpdatesAsInner(form.action);
    if (deleteSubmission(event, form)) optimisticallyRemoveCanvasAssignedJob(form.action);
  });

  document.addEventListener("turbo:submit-end", (event) => {
    if (event.detail.success) return;

    rollbackOptimisticCanvasRemoval();
    clearCanvasAssignedJobInnerUpdates();
  });
  document.addEventListener("turbo:fetch-request-error", () => {
    rollbackOptimisticCanvasRemoval();
    clearCanvasAssignedJobInnerUpdates();
  });

  document.addEventListener("turbo:before-render", preservePendingCanvasViewport);
  document.addEventListener("turbo:render", finishOptimisticCanvasRemoval);
  document.addEventListener("turbo:before-cache", cancelDeferredCanvasRefresh);
}

function setupDeferredCanvasViewportPreservation() {
  document.addEventListener("turbo:fetch-request-error", () => {
    delete window.__staffingSlotsDeferredViewport;
  });
  document.addEventListener("turbo:before-render", (event) => {
    const viewport = window.__staffingSlotsDeferredViewport;
    if (!Number.isFinite(viewport?.left)) return;

    const render = event.detail.render;
    event.detail.render = async (currentElement, newElement) => {
      await render(currentElement, newElement);
      const container = document.querySelector("#staffing-slots-container");
      if (!container) return;

      const scrollBehavior = container.style.scrollBehavior;
      const scrollSnapType = container.style.scrollSnapType;
      container.style.scrollBehavior = "auto";
      container.style.scrollSnapType = "none";
      container.scrollLeft = viewport.left;
      container.style.scrollBehavior = scrollBehavior;
      container.style.scrollSnapType = scrollSnapType;
      requestAnimationFrame(() => {
        const renderedContainer = document.querySelector("#staffing-slots-container");
        if (renderedContainer) renderedContainer.scrollLeft = viewport.left;
        delete window.__staffingSlotsDeferredViewport;
      });
    };
  });
}

function deleteSubmission(event, form) {
  const fetchMethod = event.detail?.fetchOptions?.method?.toLowerCase();
  const submissionMethod = event.detail?.formSubmission?.method?.toLowerCase();
  return fetchMethod === "delete" || submissionMethod === "delete" ||
    form.querySelector('input[name="_method"][value="delete"]') != null;
}

function assignedJobActionPath(url) {
  try {
    const path = new URL(url, window.location.origin).pathname;
    return path.includes("/account/staffing/plans/slots/") && path.includes("/assigned_jobs");
  } catch {
    return false;
  }
}

function markCanvasAssignedJobUpdatesAsInner(url) {
  canvasAssignedJobUpdateRegions(url).forEach((element) => {
    element.setAttribute("performing-inner-update", "");
  });
}

function canvasAssignedJobUpdateRegions(url) {
  const path = new URL(url, window.location.origin).pathname;
  const segments = path.split("/").filter(Boolean);
  const assignedJobsIndex = segments.indexOf("assigned_jobs");
  if (assignedJobsIndex < 0) return [];

  const assignedJobId = segments[assignedJobsIndex + 1];
  if (assignedJobId) {
    const card = document.querySelector(
      `#staffing-slots-container [data-assigned-job-param="${CSS.escape(assignedJobId)}"]`
    );
    const region = card?.closest("cable-ready-updates-for[ignore-inner-updates]");
    return region ? [region] : [];
  }

  const slotsIndex = segments.indexOf("slots");
  const slotId = segments[slotsIndex + 1];
  const slot = slotId && document.querySelector(
    `#staffing-slots-container [data-slot-param="${CSS.escape(slotId)}"]`
  );
  const region = slot?.querySelector("cable-ready-updates-for[ignore-inner-updates]");
  return region ? [region] : [];
}

function clearCanvasAssignedJobInnerUpdates() {
  document.querySelectorAll("#staffing-slots-container cable-ready-updates-for[performing-inner-update]").forEach((element) => {
    element.removeAttribute("performing-inner-update");
  });
}

function assignedJobParamFromUrl(url) {
  const segments = new URL(url, window.location.origin).pathname.split("/").filter(Boolean);
  const assignedJobsIndex = segments.indexOf("assigned_jobs");
  return assignedJobsIndex < 0 ? null : segments[assignedJobsIndex + 1];
}

function optimisticallyRemoveCanvasAssignedJob(url) {
  const assignedJobParam = assignedJobParamFromUrl(url);
  if (!assignedJobParam) return;

  const card = document.querySelector(
    `#staffing-slots-container [data-assigned-job-param="${CSS.escape(assignedJobParam)}"]`
  );
  if (!card) return;

  const anchor = canvasRemovalAnchor(card);
  const viewport = captureCanvasViewport(anchor);
  pendingCanvasRemoval = {
    card,
    parent: card.parentNode,
    nextSibling: card.nextSibling,
    anchorSelector: viewport.anchorSelector,
    anchorViewportTop: viewport.anchorViewportTop,
    slotScrollLeft: viewport.slotScrollLeft,
  };
  pendingCanvasMutation = canvasAssignedJobRemovalMutation(card);

  card.remove();
  restoreCanvasViewport(pendingCanvasRemoval);
}

function canvasAssignedJobRemovalMutation(card) {
  const affectedPersonIds = Array.from(card.querySelectorAll("[data-person-id]"))
    .map((element) => element.dataset.personId)
    .filter(Boolean);
  const dependentElementIds = new Set();
  const jobOccurrenceCardIds = new Set();

  affectedPersonIds.forEach((personId) => {
    document.querySelectorAll(
      `#staffing-slots-container [data-slot-assigned-job-card] [data-person-id="${CSS.escape(personId)}"][id]`
    ).forEach((element) => dependentElementIds.add(element.id));
  });

  const jobId = card.dataset.jobId;
  if (jobId) {
    document.querySelectorAll(
      `#staffing-slots-container [data-slot-assigned-job-card][data-job-id="${CSS.escape(jobId)}"][id]`
    ).forEach((element) => {
      if (element !== card) jobOccurrenceCardIds.add(element.id);
    });
  }

  const routePaths = new Set(Array.from(card.querySelectorAll('a[href*="/job_routes/"]'))
    .map((link) => new URL(link.href, window.location.origin).pathname));
  if (routePaths.size > 0) {
    document.querySelectorAll("#staffing-slots-container [data-slot-assigned-job-card][id]").forEach((element) => {
      const sharesRoute = Array.from(element.querySelectorAll('a[href*="/job_routes/"]'))
        .some((link) => routePaths.has(new URL(link.href, window.location.origin).pathname));
      if (sharesRoute && element !== card) dependentElementIds.add(element.id);
    });
  }

  const trayItem = jobId && document.querySelector(
    `[data-plan-canvas-tasks-tray] [data-job-id="${CSS.escape(jobId)}"][id]`
  );

  return {
    type: "assigned-job-remove",
    assignedJobId: card.dataset.assignedJobId,
    jobId,
    jobTrayItemId: trayItem?.id,
    jobOccurrenceCardIds: Array.from(jobOccurrenceCardIds),
    slotParam: card.closest("[data-slot-param]")?.dataset.slotParam,
    dependentElementIds: Array.from(dependentElementIds),
  };
}

function canvasRemovalAnchor(card) {
  const cards = Array.from(card.parentElement.querySelectorAll(":scope > [data-slot-assigned-job-card]"));
  const index = cards.indexOf(card);
  return cards[index - 1] || card.closest("[data-slot-param]") || cards[index + 1];
}

function captureCanvasViewport(anchor) {
  const container = document.querySelector("#staffing-slots-container");
  return {
    anchorSelector: canvasAnchorSelector(anchor),
    anchorViewportTop: anchor?.getBoundingClientRect().top,
    slotScrollLeft: container?.scrollLeft,
  };
}

function canvasAnchorSelector(anchor) {
  if (anchor?.dataset.assignedJobParam) {
    return `[data-assigned-job-param="${CSS.escape(anchor.dataset.assignedJobParam)}"]`;
  }
  if (anchor?.dataset.slotParam) {
    return `[data-slot-param="${CSS.escape(anchor.dataset.slotParam)}"]`;
  }
  return null;
}

function restoreCanvasViewport(viewport) {
  const container = document.querySelector("#staffing-slots-container");
  if (container && Number.isFinite(viewport.slotScrollLeft)) {
    const scrollBehavior = container.style.scrollBehavior;
    const scrollSnapType = container.style.scrollSnapType;
    container.style.scrollBehavior = "auto";
    container.style.scrollSnapType = "none";
    container.scrollLeft = viewport.slotScrollLeft;
    container.style.scrollBehavior = scrollBehavior;
    container.style.scrollSnapType = scrollSnapType;
  }

  if (!viewport.anchorSelector || !Number.isFinite(viewport.anchorViewportTop)) return;
  const anchor = document.querySelector(`#staffing-slots-container ${viewport.anchorSelector}`);
  if (!anchor) return;

  const offset = anchor.getBoundingClientRect().top - viewport.anchorViewportTop;
  if (Math.abs(offset) < 0.5) return;

  // v2 locked the page by pinning document.body to position: fixed with a
  // negative top, so while a modal was open the real scroll position lived on
  // the controller as window.modal.scrollPosition and had to be adjusted
  // instead of the window. v3 locks with `html:has(dialog[open]) { overflow:
  // hidden }` and compensates the scrollbar with padding, so the window keeps
  // its own scroll position throughout and there is only one branch left.
  window.scrollBy({left: 0, top: offset, behavior: "instant"});
}

function rollbackOptimisticCanvasRemoval() {
  if (!pendingCanvasRemoval) return;

  const {card, parent, nextSibling} = pendingCanvasRemoval;
  parent.insertBefore(card, nextSibling?.parentNode === parent ? nextSibling : null);
  restoreCanvasViewport(pendingCanvasRemoval);
  pendingCanvasRemoval = null;
  pendingCanvasMutation = null;
}

function preservePendingCanvasViewport(event) {
  if (!pendingCanvasRemoval) return;

  cancelDeferredCanvasRefresh();
  const render = event.detail.render;
  event.detail.render = async (currentElement, newElement) => {
    const anchor = pendingCanvasRemoval.anchorSelector && document.querySelector(
      `#staffing-slots-container ${pendingCanvasRemoval.anchorSelector}`
    );
    const viewport = captureCanvasViewport(anchor);
    const deferredRegions = canvasRegionsToReconcile(newElement);
    removeAssignedJobFromSlotPickers(pendingCanvasMutation?.assignedJobId);
    reconcileAssignedJobOccurrenceMetadata(newElement);
    await renderCanvasMutation(currentElement, newElement, render);
    restoreCanvasViewport(viewport);
    scheduleDeferredCanvasRefresh(deferredRegions, viewport.anchorSelector);
  };
}

function finishOptimisticCanvasRemoval() {
  pendingCanvasRemoval = null;
  pendingCanvasMutation = null;
  clearCanvasAssignedJobInnerUpdates();
}

function canvasRegionsToReconcile(newElement) {
  const changedSlot = newElement.querySelector(
    `#staffing-slots-container [data-slot-param="${CSS.escape(pendingCanvasMutation?.slotParam || "")}"]`
  );
  const assignedJobsRegion = changedSlot?.querySelector("cable-ready-updates-for[ignore-inner-updates]");
  const addControl = Array.from(
    assignedJobsRegion?.querySelectorAll("[id^='inline-create-assigned-job-']") || []
  ).find((element) => /^inline-create-assigned-job-\d+$/.test(element.id));
  const staffingSummary = changedSlot?.querySelector("[id^='canvas_slot_staffing_summary_']");
  const lastAssignmentWasRemoved = !document.querySelector(
    "#staffing-slots-container [data-slot-assigned-job-card]"
  );
  const regionIds = [
    lastAssignmentWasRemoved ? assignedJobsRegion?.id : staffingSummary?.id,
    reconcileJobTrayPlacement(newElement),
    ...(pendingCanvasMutation?.dependentElementIds || []),
  ].filter(Boolean);

  reconcileAssignedJobPickerOption(addControl);

  return Array.from(new Set(regionIds)).flatMap((id) => {
    const element = newElement.querySelector(`#${CSS.escape(id)}`);
    if (!element) return [];

    // Idiomorph builds its persistent-id map from the source element's tree.
    // Supplying an element that is still attached to the detached response body
    // can therefore pull unrelated page nodes into an otherwise scoped morph.
    return [{id, newElement: element.cloneNode(true)}];
  });
}

function reconcileJobTrayPlacement(newElement) {
  const itemId = pendingCanvasMutation?.jobTrayItemId;
  if (!itemId) return null;

  const currentItem = document.getElementById(itemId);
  const newItem = newElement.querySelector(`#${CSS.escape(itemId)}`);
  if (!currentItem || !newItem) return null;

  const newSection = newItem.closest("[data-plan-canvas-task-tray-section]");
  const currentSection = newSection?.id && document.getElementById(newSection.id);
  if (currentSection && currentItem.parentElement !== currentSection) {
    const nextItem = Array.from(newSection.children)
      .slice(Array.from(newSection.children).indexOf(newItem) + 1)
      .map((element) => element.id && document.getElementById(element.id))
      .find((element) => element?.parentElement === currentSection);
    currentSection.insertBefore(currentItem, nextItem || null);
  }

  newElement.querySelectorAll(
    "[data-plan-canvas-task-tray-section][id], [data-plan-canvas-task-tray-context][id]"
  ).forEach((newSectionElement) => {
    document.getElementById(newSectionElement.id)?.classList.toggle(
      "hidden",
      newSectionElement.classList.contains("hidden")
    );
  });

  return itemId;
}

function reconcileAssignedJobPickerOption(newAddControl) {
  const jobId = pendingCanvasMutation?.jobId;
  if (!jobId || !newAddControl) return;

  const currentAddControl = document.getElementById(newAddControl.id);
  const optionSelector = `option[value="${CSS.escape(jobId)}"]`;
  const currentOption = currentAddControl?.querySelector(optionSelector);
  const newOption = newAddControl.querySelector(optionSelector);
  if (!currentOption || !newOption) return;

  currentOption.textContent = newOption.textContent;
  syncAttributes(currentOption, newOption, ["data-job-name", "data-occurrence-count"]);
}

function reconcileAssignedJobOccurrenceMetadata(newElement) {
  (pendingCanvasMutation?.jobOccurrenceCardIds || []).forEach((id) => {
    const currentCard = document.getElementById(id);
    const newCard = newElement.querySelector(`#${CSS.escape(id)}`);
    if (!currentCard || !newCard) return;

    const currentInlineEdit = currentCard.querySelector('[data-controller~="inline-edit"]');
    const newInlineEdit = newCard.querySelector('[data-controller~="inline-edit"]');
    if (currentInlineEdit && newInlineEdit) {
      syncAttributes(currentInlineEdit, newInlineEdit, [
        "data-inline-edit-confirm-message-value",
        "data-inline-edit-save-on-click-outside-value",
      ]);
    }

    const currentScope = currentCard.querySelector("[data-plan-job-rename-scope]");
    const newScope = newCard.querySelector("[data-plan-job-rename-scope]");
    if (currentScope && newScope) {
      currentScope.textContent = newScope.textContent;
      syncAttributes(currentScope, newScope, ["data-occurrence-count"]);
    }
  });
}

function syncAttributes(currentElement, newElement, names) {
  names.forEach((name) => {
    if (newElement.hasAttribute(name)) {
      currentElement.setAttribute(name, newElement.getAttribute(name));
    } else {
      currentElement.removeAttribute(name);
    }
  });
}

function removeAssignedJobFromSlotPickers(assignedJobId) {
  if (!assignedJobId) return;

  document.querySelectorAll(
    `[data-slot-people-staging] select option[value="${CSS.escape(assignedJobId)}"]`
  ).forEach((option) => option.remove());
}

async function renderCanvasMutation(currentElement, newElement, fallbackRender) {
  if (pendingCanvasMutation?.type !== "assigned-job-remove") {
    await fallbackRender(currentElement, newElement);
    return;
  }

  const currentFlash = currentElement.querySelector("#flash");
  const newFlash = newElement.querySelector("#flash");
  if (currentFlash && newFlash) Turbo.morphElements(currentFlash, newFlash.cloneNode(true));
}

function scheduleDeferredCanvasRefresh(regions, anchorSelector) {
  if (regions.length === 0) {
    notifyCanvasRefreshComplete();
    return;
  }

  const refresh = {
    anchorSelector,
    cancelled: false,
    handle: null,
    regions: [...regions],
    scheduler: null,
    startedAt: performance.now(),
  };
  deferredCanvasRefresh = refresh;
  document.documentElement.dataset.canvasAuthoritativeRefreshing = "true";

  const reconcileNextRegion = () => {
    refresh.handle = null;
    refresh.scheduler = null;
    if (refresh.cancelled || deferredCanvasRefresh !== refresh) return;

    const region = refresh.regions.shift();
    const currentElement = region && document.getElementById(region.id);
    if (currentElement?.isConnected) {
      const regionStartedAt = performance.now();
      document.dispatchEvent(new CustomEvent("canvas:authoritative-region-start", {
        detail: {id: region.id},
      }));
      const anchor = anchorSelector && document.querySelector(
        `#staffing-slots-container ${anchorSelector}`
      );
      const viewport = captureCanvasViewport(anchor);
      if (currentElement.matches("cable-ready-updates-for")) {
        Turbo.morphChildren(currentElement, region.newElement);
      } else {
        Turbo.morphElements(currentElement, region.newElement);
      }
      restoreCanvasViewport(viewport);
      document.dispatchEvent(new CustomEvent("canvas:authoritative-region-complete", {
        detail: {id: region.id, duration: performance.now() - regionStartedAt},
      }));
    }

    if (refresh.regions.length > 0) {
      scheduleCanvasIdleWork(refresh, reconcileNextRegion);
    } else {
      deferredCanvasRefresh = null;
      delete document.documentElement.dataset.canvasAuthoritativeRefreshing;
      notifyCanvasRefreshComplete(performance.now() - refresh.startedAt);
    }
  };

  scheduleCanvasIdleWork(refresh, reconcileNextRegion);
}

function scheduleCanvasIdleWork(refresh, callback) {
  refresh.scheduler = "animation-frame";
  refresh.handle = window.requestAnimationFrame(callback);
}

function cancelDeferredCanvasRefresh() {
  const refresh = deferredCanvasRefresh;
  if (!refresh) return;

  refresh.cancelled = true;
  if (refresh.scheduler === "animation-frame" && refresh.handle != null) {
    window.cancelAnimationFrame(refresh.handle);
  }
  deferredCanvasRefresh = null;
  delete document.documentElement.dataset.canvasAuthoritativeRefreshing;
}

function notifyCanvasRefreshComplete(duration = 0) {
  document.dispatchEvent(new CustomEvent("canvas:authoritative-refresh-complete", {
    detail: {duration},
  }));
}

Turbo.StreamActions.custom_refresh = function () {
  const refreshUrl = resolveRefreshUrl(this);
  const delayMs = Math.max(0, Number.parseInt(this.getAttribute("delay-ms"), 10) || 0);

  requestPageRefresh(refreshUrl, {
    bypassCoalesce: this.getAttribute("state") === "current",
    preserveScroll: this.getAttribute("preserve-scroll") === "true",
    delayMs,
  });
};

export function requestPageRefresh(refreshUrl, {bypassCoalesce = false, preserveScroll = false, delayMs = 0} = {}) {
  const urlNorm = normalizeUrl(refreshUrl);
  const scrollPosition = preserveScroll ? currentPageScrollPosition() : null;

  if (refreshInProgress) {
    refreshLog("skip (visit already in progress)", { url: urlNorm });
    return;
  }

  // Skip duplicate CableReady refresh for same URL we just loaded (normalize for comparison)
  const now = Date.now();
  if (
    !bypassCoalesce &&
    lastRefreshedUrl != null &&
    lastRefreshCompletedAt > 0 &&
    lastUserInteractionAt <= lastRefreshCompletedAt &&
    now - lastRefreshCompletedAt < SAME_URL_COALESCE_MS &&
    urlNorm === normalizeUrl(lastRefreshedUrl)
  ) {
    refreshLog("skip (same URL recently loaded, coalesce)", {
      url: urlNorm,
      msSinceLoad: now - lastRefreshCompletedAt,
    });
    return;
  }

  setupRefreshListeners();
  if (scrollPosition) addScrollRestoreListeners(scrollPosition);
  refreshInProgress = true;
  refreshSafetyTimeout = setTimeout(clearRefreshInProgress, REFRESH_SAFETY_MS);
  const visit = () => {
    pendingRefreshTimer = null;
    refreshLog("Turbo.session.visit (from turbo stream response)", { url: urlNorm });
    Turbo.session.visit(refreshUrl, {
      action: "replace",
      shouldCacheSnapshot: false,
    });
  };

  if (delayMs > 0) {
    refreshLog("schedule Turbo.session.visit", {url: urlNorm, delayMs});
    pendingRefreshTimer = setTimeout(visit, delayMs);
  } else {
    visit();
  }
}

function resolveRefreshUrl(streamElement) {
  const currentUrl = new URL(window.location.href, window.location.origin);
  const streamUrl = streamElement.getAttribute("url") || currentUrl.href;
  const requestedUrl = new URL(streamUrl, window.location.origin);

  if (streamElement.getAttribute("state") === "current") {
    // Keep live inline/factory state while allowing the server to overlay a
    // small piece of response state, such as a contextual suggestion token.
    if (streamElement.hasAttribute("url")) {
      requestedUrl.searchParams.forEach((value, key) => {
        currentUrl.searchParams.set(key, value);
      });
    }
    return currentUrl.href;
  }

  // If the refresh is targeting the current page and the user currently has
  // inline-state query params (like ?expand=...), preserve that current state
  // instead of allowing a later plain-path refresh to wipe it out.
  if (
    currentUrl.origin === requestedUrl.origin &&
    currentUrl.pathname === requestedUrl.pathname &&
    currentUrl.search &&
    !requestedUrl.search
  ) {
    return currentUrl.href;
  }

  return requestedUrl.href;
}

function markUserInteraction() {
  lastUserInteractionAt = Date.now();
}

function normalizeUrl(url) {
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.delete("_debug_refresh_source");
    const search = normalizedSearch(u.searchParams);
    return u.origin + u.pathname + (search ? `?${search}` : "");
  } catch {
    return url;
  }
}

function normalizedSearch(searchParams) {
  const entries = Array.from(searchParams.entries()).sort(([leftKey, leftValue], [rightKey, rightValue]) => {
    if (leftKey === rightKey) return leftValue.localeCompare(rightValue);
    return leftKey.localeCompare(rightKey);
  });

  return new URLSearchParams(entries).toString();
}

function currentPageScrollPosition() {
  // Same as above: under v3 the window is never scroll-pinned while a modal is
  // open, so window.scrollY is always the honest answer.
  return {x: window.scrollX, y: window.scrollY};
}

function addScrollRestoreListeners(scrollPosition) {
  const removeScrollRestoreListeners = () => {
    document.documentElement.removeEventListener("turbo:render", restoreScroll);
    document.documentElement.removeEventListener("turbo:load", finishScrollRestore);
  };

  const cleanupScrollRestore = () => {
    removeScrollRestoreListeners();
    if (removePendingScrollRestore === cleanupScrollRestore) {
      removePendingScrollRestore = null;
    }
  };

  const restoreScroll = () => {
    window.scrollTo({left: scrollPosition.x, top: scrollPosition.y, behavior: "instant"})
  };

  const finishScrollRestore = () => {
    restoreScroll();
    cleanupScrollRestore();

    requestAnimationFrame(() => {
      restoreScroll();
      requestAnimationFrame(() => {
        restoreScroll();
      });
    });
  };

  if (removePendingScrollRestore) removePendingScrollRestore();
  document.documentElement.addEventListener("turbo:render", restoreScroll);
  document.documentElement.addEventListener("turbo:load", finishScrollRestore, {once: true});
  removePendingScrollRestore = cleanupScrollRestore;
}
